/**
 * Single source of truth for all signal / flag / score colors.
 * Change BUY/HOLD/SELL colors or flag colors here — they propagate everywhere.
 */

// ─── Bracket (red/green flag) colors ─────────────────────────────────────────

export const FLAG_COLORS: Record<number, string> = {
  5: '#85A010',
  4: '#A2C614',
  3: '#808080',
  2: '#F2850E',
  1: '#C00000',
};

export function getBracketColor(bracket: number | undefined): string {
  if (!bracket) return 'gray';
  const colors = ['dark-red', 'dark-red', 'gray', 'dark-green', 'dark-green'];
  return colors[bracket - 1] || 'gray';
}

export function getBracketArrow(bracket: number | undefined): string {
  if (!bracket) return '';
  if (bracket === 1) return 'bi-chevron-double-down';
  if (bracket === 2) return 'bi-chevron-down';
  if (bracket === 4) return 'bi-chevron-up';
  if (bracket === 5) return 'bi-chevron-double-up';
  return '';
}

export function getBracketTooltip(bracket: number | undefined): string {
  if (!bracket) return '';
  const tooltips: Record<number, string> = {
    1: 'Among the bottom 5% globally.',
    2: 'Among the bottom 10% globally.',
    4: 'Among the top 10% globally.',
    5: 'Among the top 5% globally.',
  };
  return tooltips[bracket] || '';
}

export function getFlagImage(bracket: number | undefined): string {
  if (!bracket) return '';
  const map: Record<number, string> = {
    1: 'assets/red_green_flags/Red2.svg',
    2: 'assets/red_green_flags/Red1.svg',
    3: 'assets/red_green_flags/NoFlag.svg',
    4: 'assets/red_green_flags/Green1.svg',
    5: 'assets/red_green_flags/Green2.svg',
  };
  return map[bracket] || '';
}

export function getFlagColor(bracket: number | undefined): string {
  if (!bracket) return '#000';
  return FLAG_COLORS[bracket] || '#000';
}

// ─── BUY / HOLD / SELL signal colors ─────────────────────────────────────────

export type InnovationSignal = 'BUY' | 'HOLD' | 'SELL';

/** Main (text / bar / chart) color for each signal */
export const SIGNAL_COLORS: Record<InnovationSignal, string> = {
  BUY:  '#a2c614',
  HOLD: '#d97706',
  SELL: '#dc2626',
};

/** Light background tint used in badge chips */
export const SIGNAL_BG_COLORS: Record<InnovationSignal, string> = {
  BUY:  '#f4fadb',
  HOLD: '#fef9c3',
  SELL: '#fee2e2',
};

/** Card background (exec-summary signal card) */
export const SIGNAL_CARD_BG_COLORS: Record<InnovationSignal, string> = {
  BUY:  '#f0fdf4',
  HOLD: '#fefce8',
  SELL: '#fef2f2',
};

/** Derive BUY/HOLD/SELL from a 0-100 innovation score */
export function getSignalFromScore(score: number | null | undefined): InnovationSignal | null {
  if (score == null) return null;
  if (score >= 75) return 'BUY';
  if (score >= 25) return 'HOLD';
  return 'SELL';
}

/** Returns the main signal color (hex) for a signal string */
export function getSignalColor(signal: InnovationSignal | string | null | undefined): string {
  if (signal === 'BUY' || signal === 'HOLD' || signal === 'SELL') return SIGNAL_COLORS[signal];
  return SIGNAL_COLORS.SELL;
}

/** Returns the light badge background color for a signal */
export function getSignalBgColor(signal: InnovationSignal | string | null | undefined): string {
  if (signal === 'BUY' || signal === 'HOLD' || signal === 'SELL') return SIGNAL_BG_COLORS[signal];
  return SIGNAL_BG_COLORS.SELL;
}

/** Returns the card background color for a signal (exec-summary) */
export function getSignalCardBg(signal: InnovationSignal | string | null | undefined): string {
  if (signal === 'BUY' || signal === 'HOLD' || signal === 'SELL') return SIGNAL_CARD_BG_COLORS[signal];
  return SIGNAL_CARD_BG_COLORS.SELL;
}

/** Returns the main signal color (hex) for a 0-100 innovation score */
export function getBarColor(score: number | null | undefined): string {
  const signal = getSignalFromScore(score);
  return signal ? SIGNAL_COLORS[signal] : SIGNAL_COLORS.SELL;
}

// ─── S-Curve stage colors ────────────────────────────────────────────────────

export type StageName = 'Emerging' | 'Accelerating' | 'Maturing' | 'Declining' | 'No Stage';

/** Solid hex colors for chart bars, badges, etc. */
export const STAGE_COLORS: Record<StageName, string> = {
  Emerging:     '#3b82f6',
  Accelerating: '#22c55e',
  Maturing:     '#eab308',
  Declining:    '#ef4444',
  'No Stage':   '#9ca3af',
};

/** Light fills for chart background bands */
export const STAGE_BG_COLORS: Record<StageName, string> = {
  Emerging:     'rgba(59, 130, 246, 0.22)',
  Accelerating: 'rgba(34, 197, 94, 0.22)',
  Maturing:     'rgba(234, 179, 8, 0.22)',
  Declining:    'rgba(239, 68, 68, 0.22)',
  'No Stage':   'rgba(156, 163, 175, 0.22)',
};

/** Legend / medium-opacity variant */
export const STAGE_LEGEND_COLORS: Record<StageName, string> = {
  Emerging:     'rgba(59, 130, 246, 0.6)',
  Accelerating: 'rgba(34, 197, 94, 0.6)',
  Maturing:     'rgba(234, 179, 8, 0.6)',
  Declining:    'rgba(239, 68, 68, 0.6)',
  'No Stage':   'rgba(156, 163, 175, 0.6)',
};

export function getStageColor(stage: string): string {
  return STAGE_COLORS[stage as StageName] || '#9ca3af';
}

export function getStageBgColor(stage: string): string {
  return STAGE_BG_COLORS[stage as StageName] || 'rgba(200, 200, 200, 0.1)';
}

export function getStageLegendColor(stage: string): string {
  return STAGE_LEGEND_COLORS[stage as StageName] || 'rgba(200, 200, 200, 0.6)';
}

export function getCompetitivenessInfo(score: number): { color: string; text: string } {
  let text: string;
  if (score >= 75) text = 'Strong Innovative Competitiveness';
  else if (score >= 50) text = 'Above Average Innovative Competitiveness';
  else if (score >= 25) text = 'Below Average Innovative Competitiveness';
  else text = 'Weak Innovative Competitiveness';

  return { color: getBarColor(score), text };
}
